import React, { useMemo } from "react"

import { HStack } from "@/nzds/layout"

import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward"
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward"
import InfoRoundedIcon from "@mui/icons-material/InfoRounded"
import { Tooltip, Typography, useTheme } from "@mui/material"

import { Decimal } from "../decimal/decimal"
import { If } from "../if/if"

interface GetPercentChangeDescriptionProps {
  isGreaterThan: boolean
  isLessThan: boolean
  isNeutral: boolean
}

const getPercentChangeDescription = ({
  isGreaterThan,
  isLessThan,
  isNeutral,
}: GetPercentChangeDescriptionProps): string => {
  if (isGreaterThan) {
    return "Higher"
  } else if (isLessThan) {
    return "Lower"
  } else if (isNeutral) {
    return "No change"
  }

  return "Unknown"
}

interface PercentChangeProps {
  /**
   * The percent change (e.g 21.3)
   */
  value: number | null | undefined
}

export const PercentChange = ({ value }: PercentChangeProps) => {
  const theme = useTheme()

  const validValue = value != null && Number.isFinite(value) ? value : undefined
  const isValidPercentChange = validValue !== undefined
  const isGreaterThan = validValue !== undefined && validValue > 0
  const isLessThan = validValue !== undefined && validValue < 0
  const isNeutral = validValue !== undefined && validValue === 0

  const percentChangeDescription = useMemo<string>(
    () =>
      getPercentChangeDescription({
        isGreaterThan,
        isLessThan,
        isNeutral,
      }),
    [isGreaterThan, isLessThan, isNeutral]
  )

  return (
    <HStack
      spacing={0.5}
      sx={{
        alignItems: "center",
        ...(isGreaterThan ? { color: theme.palette.spectrum.red[400] } : {}),
        ...(isLessThan ? { color: theme.palette.spectrum.green[400] } : {}),
        ...(isNeutral || !isValidPercentChange
          ? { color: theme.palette.grey[300] }
          : {}),
      }}
    >
      <If condition={isGreaterThan}>
        <ArrowUpwardIcon fontSize="small" />
      </If>
      <If condition={isLessThan}>
        <ArrowDownwardIcon fontSize="small" />
      </If>
      <If condition={!isValidPercentChange}>
        <Tooltip
          placement="bottom-start"
          title="Not enough data to calculate percent change"
        >
          <InfoRoundedIcon fontSize="small" />
        </Tooltip>
      </If>
      <Typography component="span">
        <Decimal
          format={{ precision: 0, style: "percent" }}
          value={validValue !== undefined ? Math.abs(validValue) : value}
        />
        <If condition={percentChangeDescription}>
          {" "}
          {percentChangeDescription}
        </If>
      </Typography>
    </HStack>
  )
}
